import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import './review.css';

const CLAMP_LINES = 6;
// jsdom doesn't compute layout, so "would this actually overflow 6 lines" isn't
// testable via measured height — a rough character-length heuristic decides
// whether the "show more" toggle appears at all (mirrors the intent: only
// offer to expand text that plausibly needed clamping). #414 part 2 (conditional
// show-more) is this `canClamp` gate; the toggle is suppressed for short text.
const SHOW_MORE_THRESHOLD = 240;

/**
 * Description body for an ItemBlock row. Descriptions are stored as **markdown**
 * (the DB/MDXEditor contract, #333) so we render them as markdown via
 * `react-markdown` — the app's existing markdown renderer — instead of the old
 * plain-text path (#414 part 1). react-markdown is XSS-safe by default (no
 * rehype-raw), so embedded HTML is escaped, not executed. The rendered block is
 * clamped to ~6 lines with a "show more"/"show less" toggle that appears only for
 * long content (spec §Breakdown rendering).
 */
function ClampedText({ text = '' }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const canClamp = text.length > SHOW_MORE_THRESHOLD;
  return (
    <div data-testid="clamped-text">
      <div
        className="bddesc"
        style={
          expanded || !canClamp
            ? undefined
            : {
              display: '-webkit-box',
              WebkitLineClamp: CLAMP_LINES,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
        }
      >
        <ReactMarkdown>{text}</ReactMarkdown>
      </div>
      {canClamp && (
        // "Show more"/"Show less" isn't part of the mock (docs/design/specimen/
        // review.html has no clamping) — kept as a muted `.linkbtn` so it reads
        // as a secondary affordance, distinct from `.bdctl`'s accent-coloured
        // "+ Comment" (#450 / #414 Part 3).
        <button
          type="button"
          className="linkbtn muted"
          onClick={() => setExpanded((v) => !v)}
          data-testid="clamp-toggle"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

ClampedText.propTypes = {
  text: PropTypes.string,
};

export default ClampedText;
